/** A single geolocation fix in [lng, lat] order (deck.gl/MapLibre convention). */
export interface GeoFix {
  coords: [number, number];
  accuracyMeters: number;
}

export type GeoStatus = 'idle' | 'locating' | 'tracking' | 'denied' | 'error';

export interface GeoState {
  fix: GeoFix | null;
  status: GeoStatus;
}

/** The slice of the Geolocation API we use — injected so it can be faked in tests. */
export interface GeoProvider {
  watchPosition(
    success: (pos: GeolocationPosition) => void,
    error?: (err: GeolocationPositionError) => void,
    options?: PositionOptions,
  ): number;
  clearWatch(id: number): void;
}

const PERMISSION_DENIED = 1;

/** browser geolocation, or null in an insecure/unsupported context. */
function defaultProvider(): GeoProvider | null {
  if (typeof navigator !== 'undefined' && navigator.geolocation) {
    return navigator.geolocation;
  }
  return null;
}

const WATCH_OPTIONS: PositionOptions = {
  enableHighAccuracy: true,
  maximumAge: 10_000,
  timeout: 15_000,
};

/** Owns the browser geolocation lifecycle and exposes the latest fix. */
export class GeoTracker {
  private state: GeoState = { fix: null, status: 'idle' };
  private listeners = new Set<() => void>();
  private watchId: number | null = null;

  constructor(private readonly provider: GeoProvider | null = defaultProvider()) {}

  getState(): GeoState {
    return this.state;
  }

  /** Begin live tracking. Idempotent — a second call while watching is a no-op. */
  start(): void {
    if (this.watchId !== null) return;
    if (!this.provider) {
      this.set({ ...this.state, status: 'error' });
      return;
    }
    this.set({ ...this.state, status: 'locating' });
    this.watchId = this.provider.watchPosition(
      (pos) =>
        this.set({
          fix: {
            coords: [pos.coords.longitude, pos.coords.latitude],
            accuracyMeters: pos.coords.accuracy,
          },
          status: 'tracking',
        }),
      (err) =>
        this.set({
          ...this.state,
          status: err.code === PERMISSION_DENIED ? 'denied' : 'error',
        }),
      WATCH_OPTIONS,
    );
  }

  stop(): void {
    if (this.watchId === null) return;
    this.provider?.clearWatch(this.watchId);
    this.watchId = null;
  }

  onChange(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  private set(next: GeoState): void {
    this.state = next;
    for (const cb of this.listeners) cb();
  }
}
